import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { format, isValid, parse } from "date-fns";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Ripple } from "primereact/ripple";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";

import {
  changeReminderStatusApi,
  createReminderApi,
  deleteReminderApi,
  getReminderListApi,
  updateReminderApi,
} from "../../Api/Reminder/ReminderApi";
import NoDataCreated from "../../Components/NoDataCreated";
import PageLoader from "../../Components/PageLoader";
import Textarea from "../../Components/TextArea";
import TextInput from "../../Components/TextInput";
import { goToTop } from "../../Components/goToTop";
import { RootState } from "../../Controllers/Store";
import { MenuBarPermission } from "../../Controllers/Types/NewPermission/MenuBarPermission";
import {
  Reminder as ReminderModel,
  ReminderPayload,
  ReminderStatus,
} from "../../Controllers/Types/Reminder/ReminderInterface";
import "./Reminder.css";

const reminderStatuses: ReminderStatus[] = [
  "Pending",
  "Processing",
  "Completed",
];

const parseApiDateTime = (value?: string | null): Date | null => {
  if (!value) return null;

  for (const pattern of ["yyyy-MM-dd HH:mm:ss", "yyyy-MM-dd HH:mm"]) {
    const parsed = parse(value, pattern, new Date());
    if (isValid(parsed)) return parsed;
  }

  return null;
};

const getReminderStatus = (value?: string | null): ReminderStatus => {
  if (value === "Processing") return "Processing";
  if (value === "Completed") return "Completed";
  return "Pending";
};

const Reminder: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useRef<Toast>(null);

  const [loadingPage, setLoadingPage] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusLoadingId, setStatusLoadingId] = useState<number | null>(null);
  const [reminders, setReminders] = useState<ReminderModel[]>([]);
  const [dateFilter, setDateFilter] = useState<Date | null>(new Date());
  const [searchKey, setSearchKey] = useState("");
  const [statusFilter, setStatusFilter] = useState<"All" | ReminderStatus>("All");

  const [showReminderModal, setShowReminderModal] = useState(false);
  const [formMode, setFormMode] = useState<"Add" | "Edit">("Add");
  const [selectedReminder, setSelectedReminder] = useState<ReminderModel | null>(null);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [reminderDateTime, setReminderDateTime] = useState<Date | null>(null);
  const [notifyDuration, setNotifyDuration] = useState<number>(1);

  const [titleError, setTitleError] = useState("");
  const [reminderDateTimeError, setReminderDateTimeError] = useState("");
  const [notifyDurationError, setNotifyDurationError] = useState("");

  const menuPermission = location.state?.menuPermission as
    | MenuBarPermission
    | undefined;
  const isViewOnly =
    menuPermission?.view === true && menuPermission?.have_feature === false;

  const permissionScreens = useSelector(
    (state: RootState) => state.permission.permissions
  );
  const reminderPermission = permissionScreens.find((screen) => {
    const screenName = screen.screen_name.trim().toLowerCase();
    return screenName === "reminder" || screenName === "remainder";
  });
  const hasPermissionConfig = Boolean(reminderPermission);
  const hasActionPermission = (...permissionNames: string[]) => {
    if (!hasPermissionConfig) return true;
    return permissionNames.some((permission) =>
      reminderPermission?.permissions.includes(permission)
    );
  };

  const canAdd =
    !isViewOnly && hasActionPermission("add_reminder", "add_remainder");
  const canEdit =
    !isViewOnly && hasActionPermission("edit_reminder", "edit_remainder");
  const canDelete =
    !isViewOnly && hasActionPermission("delete_reminder", "delete_remainder");
  const canChangeStatus =
    !isViewOnly &&
    hasActionPermission(
      "status_changed",
      "status_change",
      "change_status",
      "reminder_status_changed",
      "remainder_status_changed"
    );

  const showToast = useCallback((
    severity: "success" | "error" | "warn" | "info",
    summary: string,
    detail: string
  ) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  }, []);

  const loadReminders = useCallback(async (showLoader = true) => {
    if (showLoader) setLoadingPage(true);
    const response = await getReminderListApi();
    if (showLoader) setLoadingPage(false);

    if (response.status) {
      const list = Array.isArray(response.data)
        ? (response.data as ReminderModel[])
        : [];
      setReminders(
        [...list].sort((first, second) =>
          first.date_time.localeCompare(second.date_time)
        )
      );
      return;
    }

    showToast(
      "error",
      "Unable to load reminders",
      response.massage ?? "Please try again later."
    );
  }, [showToast]);

  useEffect(() => {
    loadReminders();
  }, [loadReminders]);

  const filteredReminders = useMemo(() => {
    const query = searchKey.trim().toLowerCase();

    return reminders.filter((reminder) => {
      const reminderDate = parseApiDateTime(reminder.date_time);
      const matchesDate =
        !dateFilter ||
        (reminderDate &&
          format(reminderDate, "yyyy-MM-dd") ===
            format(dateFilter, "yyyy-MM-dd"));
      const matchesStatus =
        statusFilter === "All" ||
        getReminderStatus(reminder.status) === statusFilter;
      const matchesSearch =
        !query ||
        reminder.title.toLowerCase().includes(query) ||
        String(reminder.note ?? "").toLowerCase().includes(query);

      return matchesDate && matchesStatus && matchesSearch;
    });
  }, [dateFilter, reminders, searchKey, statusFilter]);

  const clearForm = () => {
    setSelectedReminder(null);
    setTitle("");
    setNote("");
    setReminderDateTime(null);
    setNotifyDuration(1);
    setTitleError("");
    setReminderDateTimeError("");
    setNotifyDurationError("");
  };

  const closeReminderModal = () => {
    if (saving) return;
    setShowReminderModal(false);
    clearForm();
  };

  const openAddReminderModal = () => {
    const dateTime = new Date();
    dateTime.setHours(dateTime.getHours() + 1, 0, 0, 0);

    clearForm();
    setFormMode("Add");
    setReminderDateTime(dateTime);
    setShowReminderModal(true);
  };

  const openEditReminderModal = (reminder: ReminderModel) => {
    clearForm();
    setFormMode("Edit");
    setSelectedReminder(reminder);
    setTitle(reminder.title ?? "");
    setNote(reminder.note ?? "");
    setReminderDateTime(parseApiDateTime(reminder.date_time));
    setNotifyDuration(Number(reminder.notify_interval) || 1);
    setShowReminderModal(true);
  };

  const validateForm = () => {
    let valid = true;
    setTitleError("");
    setReminderDateTimeError("");
    setNotifyDurationError("");

    if (!title.trim()) {
      setTitleError("Enter reminder title");
      valid = false;
    }
    if (!reminderDateTime || !isValid(reminderDateTime)) {
      setReminderDateTimeError("Select reminder date and time");
      valid = false;
    }
    if (!Number.isInteger(notifyDuration) || notifyDuration < 1) {
      setNotifyDurationError("Enter a valid notify duration");
      valid = false;
    }

    return valid;
  };

  const saveReminder = async () => {
    if (!validateForm() || !reminderDateTime) return;

    const payload: ReminderPayload = {
      title: title.trim(),
      note: note.trim(),
      date_time: format(reminderDateTime, "yyyy-MM-dd HH:mm"),
      // notify_time remains in the API contract but is no longer a user input.
      notify_time: format(reminderDateTime, "yyyy-MM-dd HH:mm"),
      notify_interval: notifyDuration,
    };

    setSaving(true);
    const response =
      formMode === "Add"
        ? await createReminderApi(payload)
        : await updateReminderApi(selectedReminder!.id, payload);
    setSaving(false);

    if (!response.status) {
      showToast(
        "error",
        "Unable to save reminder",
        response.massage ?? "Please check the entered values."
      );
      return;
    }

    setShowReminderModal(false);
    clearForm();
    await loadReminders(false);
    showToast(
      "success",
      "Success",
      formMode === "Add"
        ? "Reminder created successfully."
        : "Reminder updated successfully."
    );
  };

  const changeStatus = async (
    reminder: ReminderModel,
    status: ReminderStatus
  ) => {
    if (getReminderStatus(reminder.status) === status) return;

    setStatusLoadingId(reminder.id);
    const response = await changeReminderStatusApi(reminder.id, status);
    setStatusLoadingId(null);

    if (!response.status) {
      showToast(
        "error",
        "Unable to change status",
        response.massage ?? "Please try again later."
      );
      return;
    }

    setReminders((current) =>
      current.map((item) =>
        item.id === reminder.id ? { ...item, status } : item
      )
    );
    showToast("success", "Success", `Reminder changed to ${status}.`);
  };

  const requestStatusChange = (
    reminder: ReminderModel,
    status: ReminderStatus
  ) => {
    confirmDialog({
      header: "Change reminder status",
      message: `Change this reminder status to ${status}?`,
      icon: "bi bi-info-circle",
      acceptLabel: "Change",
      rejectLabel: "Cancel",
      defaultFocus: "accept",
      dismissableMask: true,
      accept: () => changeStatus(reminder, status),
    });
  };

  const removeReminder = async (reminder: ReminderModel) => {
    const response = await deleteReminderApi(reminder.id);

    if (!response.status) {
      showToast(
        "error",
        "Unable to delete reminder",
        response.massage ?? "Please try again later."
      );
      return;
    }

    setReminders((current) =>
      current.filter((item) => item.id !== reminder.id)
    );
    showToast("success", "Success", "Reminder deleted successfully.");
  };

  const requestDelete = (reminder: ReminderModel) => {
    confirmDialog({
      header: "Delete reminder",
      message: "Are you sure you want to delete this reminder? This action cannot be undone.",
      icon: "bi bi-exclamation-triangle",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      defaultFocus: "reject",
      dismissableMask: true,
      accept: () => removeReminder(reminder),
    });
  };

  const displayDateTime = (value: string) => {
    const parsed = parseApiDateTime(value);
    return parsed ? format(parsed, "dd MMM yyyy, HH:mm") : "---";
  };

  const statusBody = (reminder: ReminderModel) => {
    const status = getReminderStatus(reminder.status);

    if (!canChangeStatus) {
      return (
        <span className={`reminder_status_label status_${status.toLowerCase()}`}>
          {status}
        </span>
      );
    }

    return (
      <Dropdown
        value={status}
        options={reminderStatuses}
        onChange={(event: DropdownChangeEvent) =>
          requestStatusChange(reminder, event.value as ReminderStatus)
        }
        className={`reminder_status_dropdown status_${status.toLowerCase()}`}
        panelClassName="reminder_status_panel"
        disabled={statusLoadingId === reminder.id}
      />
    );
  };

  const actionBody = (reminder: ReminderModel) => {
    if (!canEdit && !canDelete) return null;

    return (
      <div className="table_action_btn_area reminder_action_area">
        {canEdit && (
          <button
            type="button"
            className="table_action_btn p-ripple"
            onClick={() => openEditReminderModal(reminder)}
          >
            Edit
            <Ripple />
          </button>
        )}
        {canDelete && (
          <button
            type="button"
            className="table_action_btn reminder_delete_btn p-ripple"
            onClick={() => requestDelete(reminder)}
          >
            Delete
            <Ripple />
          </button>
        )}
      </div>
    );
  };

  const modalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">
        {formMode === "Add" ? "Add reminder" : "Edit reminder"}
      </h5>
      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={closeReminderModal}
        disabled={saving}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const modalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Close"
        className="custom_btn secondary"
        onClick={closeReminderModal}
        disabled={saving}
      />
      <Button
        label={saving ? "Processing" : formMode === "Add" ? "Save" : "Update"}
        className="custom_btn primary"
        onClick={saveReminder}
        loading={saving}
      />
    </div>
  );

  return (
    <React.Fragment>
      <Toast ref={toast} />

      <div className="main_content layout_padding show_side_bar">
        {loadingPage ? (
          <PageLoader />
        ) : (
          <div className="page_content mt-0">
            <div className="d-flex align-items-center justify-content-between">
              <h4 className="main_heading">Reminder</h4>
              <button
                className="back_btn p-ripple"
                onClick={() => {
                  navigate(-1);
                  goToTop();
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Back
                <Ripple />
              </button>
            </div>

            <div className="page_header_area reminder_header_area mt-sm-3 mt-3">
              <div className="filter_option_group reminder_filter_group">
                <div className="filter_date_input_group reminder_date_filter_group">
                  <Calendar
                    id="reminderDateFilter"
                    value={dateFilter}
                    onChange={(event) =>
                      setDateFilter((event.value as Date) ?? null)
                    }
                    dateFormat="dd M yy"
                    placeholder="Select reminder date"
                    className="custom_date_input filter_date_input custom_width"
                    showIcon
                    iconPos="left"
                    icon="bi bi-calendar2-minus-fill"
                    readOnlyInput
                  />
                  {dateFilter && (
                    <i
                      className="bi bi-x date_clear_icon"
                      onClick={() => setDateFilter(null)}
                      role="button"
                      aria-label="Clear reminder date filter"
                    ></i>
                  )}
                </div>

                <div className="reminder_search_area">
                  <input
                    type="text"
                    value={searchKey}
                    onChange={(event) => setSearchKey(event.target.value)}
                    placeholder="Search title or note"
                    aria-label="Search reminders"
                  />
                  {searchKey ? (
                    <button
                      type="button"
                      onClick={() => setSearchKey("")}
                      aria-label="Clear reminder search"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  ) : (
                    <i className="bi bi-search"></i>
                  )}
                </div>

                <Dropdown
                  value={statusFilter}
                  options={["All", ...reminderStatuses]}
                  onChange={(event: DropdownChangeEvent) =>
                    setStatusFilter(event.value)
                  }
                  className="form_dropdown filter_dropdown reminder_status_filter"
                  panelClassName="status_option_panel"
                  placeholder="Status"
                />
              </div>

              {canAdd && (
                <div className="page_action_area">
                  <button
                    type="button"
                    className="add_data_link_btn p-ripple"
                    onClick={openAddReminderModal}
                  >
                    <i className="bi bi-plus-circle"></i>
                    <span>Add reminder</span>
                    <Ripple />
                  </button>
                </div>
              )}
            </div>

            <div className="page_main_content_section reminder_table_section">
              {reminders.length > 0 ? (
                <DataTable
                  value={filteredReminders}
                  paginator
                  size="small"
                  rows={10}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  emptyMessage="No reminder(s) found!"
                  tableStyle={{ minWidth: "56rem" }}
                  paginatorDropdownAppendTo="self"
                  paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                  currentPageReportTemplate="{first} to {last} of {totalRecords}"
                  className="page_table p-0 p-sm-2"
                  rowHover
                  scrollable
                >
                  <Column
                    header="Title"
                    headerClassName="text_purple"
                    field="title"
                    body={(reminder: ReminderModel) => (
                      <div className="reminder_title_cell">
                        <span>{reminder.title || "---"}</span>
                        {reminder.note && <small>{reminder.note}</small>}
                      </div>
                    )}
                    style={{ width: "27%" }}
                  />
                  <Column
                    header="Reminder date & time"
                    headerClassName="text_purple"
                    field="date_time"
                    body={(reminder: ReminderModel) => (
                      <span className="text_no_wrap">
                        {displayDateTime(reminder.date_time)}
                      </span>
                    )}
                    style={{ width: "18%" }}
                  />
                  <Column
                    header="Next notify duration (min)"
                    alignHeader="center"
                    headerClassName="text_purple"
                    field="notify_interval"
                    body={(reminder: ReminderModel) => (
                      <span className="flex_center reminder_duration_value">
                        {reminder.notify_interval ?? "---"}
                      </span>
                    )}
                    style={{ width: "10%" }}
                  />
                  <Column
                    header="Status"
                    alignHeader="center"
                    headerClassName="text_purple"
                    bodyClassName="reminder_status_column"
                    body={statusBody}
                    style={{ width: "15%" }}
                  />
                  {(canEdit || canDelete) && (
                    <Column
                      alignHeader="center"
                      body={actionBody}
                      className="table_btn_area"
                      frozen
                      alignFrozen="right"
                      style={{ width: "12%" }}
                    />
                  )}
                </DataTable>
              ) : (
                <NoDataCreated message="No reminders have been added yet" />
              )}
            </div>
          </div>
        )}
      </div>

      <Dialog
        visible={showReminderModal}
        header={modalHeader}
        footer={modalFooter}
        headerClassName="custom_modal_header"
        className="custom_modal_dialog modal_dialog_md reminder_dialog"
        onHide={closeReminderModal}
        dismissableMask={!saving}
        draggable={false}
      >
        <div className="custom_modal_body">
          <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
            <div className="row">
              <div className="col-12">
                <TextInput
                  id="reminderTitle"
                  label="Title"
                  labelHtmlFor="reminderTitle"
                  required
                  value={title}
                  placeholder="Enter reminder title"
                  onChange={(event) => {
                    setTitle(event.target.value);
                    setTitleError("");
                  }}
                  error={titleError}
                  inputAutoFocus
                  maxLength={150}
                />
              </div>

              <div className="col-12">
                <Textarea
                  id="reminderNote"
                  label="Note"
                  labelHtmlFor="reminderNote"
                  value={note}
                  rows={3}
                  placeholder="Enter reminder note"
                  onChange={(event) => setNote(event.target.value)}
                />
              </div>

              <div className="col-12 col-md-6">
                <div className="page_form_group">
                  <label className="custom_form_label is_required" htmlFor="reminderDateTime">
                    Reminder date & time
                  </label>
                  <Calendar
                    id="reminderDateTime"
                    value={reminderDateTime}
                    onChange={(event) => {
                      setReminderDateTime((event.value as Date) ?? null);
                      setReminderDateTimeError("");
                    }}
                    className="w-100 reminder_datetime_input"
                    inputClassName="custom_form_input"
                    dateFormat="dd M yy"
                    showTime
                    hourFormat="24"
                    showIcon
                    icon="bi bi-calendar2-week"
                    readOnlyInput
                    placeholder="Select date and time"
                  />
                  {reminderDateTimeError && (
                    <small className="form_error_msg">{reminderDateTimeError}</small>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="page_form_group">
                  <Tooltip target=".notify_interval_info" />
                  <label className="custom_form_label is_required reminder_duration_form_label" htmlFor="notifyDuration">
                    Next notify duration (min)
                    <i
                      className="bi bi-info-circle notify_interval_info"
                      data-pr-tooltip="Minutes to wait before reminding you again."
                      data-pr-position="top"
                      tabIndex={0}
                    ></i>
                  </label>
                  <InputNumber
                    inputId="notifyDuration"
                    value={notifyDuration}
                    onValueChange={(event) => {
                      setNotifyDuration(event.value ?? 1);
                      setNotifyDurationError("");
                    }}
                    min={1}
                    maxFractionDigits={0}
                    useGrouping={false}
                    className="w-100 reminder_duration_input"
                    inputClassName="custom_form_input w-100"
                    placeholder="Enter minutes"
                  />
                  {notifyDurationError && (
                    <small className="form_error_msg">{notifyDurationError}</small>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </React.Fragment>
  );
};

export default Reminder;
